<template>
  <div
    v-if="spread && areSpreadImagesLoaded"
    class="brochure-spread"
    :class="{ 'full-width': isLastSpread }"
  >
    <BrochurePageItem
      :key="spread[0]?.id"
      :page="spread[0] ?? null"
      :lazy="false"
      :class="{ 'page-left': !isLastSpread, 'page-center': isLastSpread }"
      :hotspot-mode="hotspotMode"
      :is-last-spread="isLastSpread"
      :is-first-spread="isFirstSpread"
    />
    <BrochurePageItem
      v-if="!isLastSpread"
      :key="spread[1]?.id"
      :page="spread[1] ?? null"
      :lazy="false"
      class="page-right"
      :hotspot-mode="hotspotMode"
      :is-first-spread="isFirstSpread"
    />
  </div>
</template>

<script setup lang="ts">
import { useImage as useImageLoading } from "@vueuse/core";
import { HotspotMode, type PageFragment } from "~/graphql/generated";

type Spread = [PageFragment | null, PageFragment | null];

const props = defineProps<{
  spread: Spread;
  hotspotMode: HotspotMode;
}>();

const { isLoading: isLeftPageLoading } = useImageLoading({
  src: props.spread[0]?.image_large || "",
});
const { isLoading: isRightPageLoading } = useImageLoading({
  src: props.spread[1]?.image_large || "",
});

const areSpreadImagesLoaded = computed(() => {
  return !isLeftPageLoading.value && !isRightPageLoading.value;
});

// If we have no left page, this is the first spread
const isFirstSpread = computed(() => {
  return !props.spread[0];
});

const isLastSpread = computed(() => {
  return !props.spread[1];
});
</script>

<style lang="scss" scoped>
.brochure-spread {
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: -1;
  position: relative;

  background-color: $light-100;

  &.current-spread {
    z-index: 2;
  }

  &.full-width {
    grid-template-columns: 1fr;
  }

  // Styling for the prev and next spread
  &.prev-spread,
  &.next-spread {
    position: absolute;
    top: 0;
    z-index: 1;

    &.page-left {
      right: 50%;
    }

    &.page-right {
      left: 50%;
    }
  }
}
</style>
